/**
 * setUploadImage - Converts a ProductImage source URL to an UploadImage object
 *
 * Extracted from client ProductForm.tsx (lines 134-143)
 * Takes a product image URL and applies service-specific URL modifiers
 * to create an UploadImage object with regular and thumbnail URLs.
 *
 * @param src - Source URL of the product image
 * @param modifiers - Image modifiers from imageModifiers function
 * @returns UploadImage object with type, url, and thumbnailUrl
 */

import type { UrlUploadImage } from "@shared/types/product";
import type { ImageModifiers } from "./image-modifiers";

/**
 * Modifiers are literal query-string fragments beginning with `&` (e.g.
 * "&height=600&width=600"), written assuming `src` already has a `?query`.
 * Detect whether that's actually true and swap in `?` when it isn't, so a
 * bare URL doesn't end up with a malformed `path.png&height=...` suffix.
 */
function appendModifier(src: string, modifier: string): string {
  return src.includes("?") ? src + modifier : src + modifier.replace(/^&/, "?");
}

export function setUploadImage(src: string, modifiers: ImageModifiers): UrlUploadImage {
  return {
    type: "url",
    url: modifiers.modifier ? appendModifier(src, modifiers.modifier) : src,
    thumbnailUrl: modifiers.thumbModifier ? appendModifier(src, modifiers.thumbModifier) : src,
  };
}
